import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import swimService from '../api/services/swimService';
import { ClassLevel, ClassLevelCreatePayload, ClassLevelUpdatePayload } from '../types/swim';
import { useIsMobile } from '../hooks/useIsMobile';
import { ResponsiveTable, ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import { useSnackbar } from '../components/common/SnackbarProvider';

type DialogMode = 'create' | 'edit' | null;

interface FormState {
  name: string;
  description: string;
  min_age: string;
  max_age: string;
  prerequisite_level_id: string;
  target_audience: 'kids' | 'adult';
}

const emptyForm: FormState = {
  name: '',
  description: '',
  min_age: '',
  max_age: '',
  prerequisite_level_id: '',
  target_audience: 'kids',
};

const ClassLevelsAdminPage: React.FC = () => {
  const isMobile = useIsMobile();
  const snackbar = useSnackbar();
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [audience, setAudience] = useState<'kids' | 'adult'>('kids');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => { void load(); }, [showInactive, audience]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await swimService.listClassLevels({ includeInactive: showInactive, audience });
      setLevels(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load class levels');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    // New level adopts the currently active tab's audience so the admin
    // doesn't have to flip the field manually.
    setForm({ ...emptyForm, target_audience: audience });
    setEditingId(null);
    setDialogMode('create');
  }
  function openEdit(lvl: ClassLevel) {
    setForm({
      name: lvl.name,
      description: lvl.description ?? '',
      min_age: lvl.min_age == null ? '' : String(lvl.min_age),
      max_age: lvl.max_age == null ? '' : String(lvl.max_age),
      prerequisite_level_id: lvl.prerequisite_level_id ?? '',
      target_audience: lvl.target_audience ?? 'kids',
    });
    setEditingId(lvl.id);
    setDialogMode('edit');
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body: ClassLevelCreatePayload | ClassLevelUpdatePayload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        target_audience: form.target_audience,
        min_age: form.min_age ? Number(form.min_age) : undefined,
        max_age: form.max_age ? Number(form.max_age) : undefined,
        prerequisite_level_id: form.prerequisite_level_id || null,
      };
      if (dialogMode === 'create') {
        await swimService.createClassLevel(body as ClassLevelCreatePayload);
      } else if (editingId) {
        await swimService.updateClassLevel(editingId, body);
      }
      setDialogMode(null);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(lvl: ClassLevel) {
    if (!window.confirm(`Deactivate "${lvl.name}"? (Soft delete — history is preserved.)`)) return;
    setBusy(true);
    try {
      await swimService.deactivateClassLevel(lvl.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Deactivate failed');
    } finally {
      setBusy(false);
    }
  }

  async function reactivate(lvl: ClassLevel) {
    setBusy(true);
    try {
      await swimService.updateClassLevel(lvl.id, { is_active: true });
      snackbar.success(`Reactivated "${lvl.name}"`);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Reactivate failed');
    } finally {
      setBusy(false);
    }
  }

  async function purge(lvl: ClassLevel) {
    if (!window.confirm(
      `Permanently DELETE "${lvl.name}"?\n\n` +
      `This cannot be undone. It will fail if the level is still in use ` +
      `by a program, assessment, or as another level's prerequisite — ` +
      `clean those up first.`,
    )) return;
    setBusy(true);
    try {
      await swimService.purgeClassLevel(lvl.id);
      snackbar.success(`Deleted "${lvl.name}" permanently`);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Permanent delete failed');
    } finally {
      setBusy(false);
    }
  }

  async function move(lvl: ClassLevel, direction: -1 | 1) {
    const visible = levels.slice().sort((a, b) => a.display_order - b.display_order);
    const idx = visible.findIndex((l) => l.id === lvl.id);
    const target = idx + direction;
    if (target < 0 || target >= visible.length) return;
    const reordered = [...visible];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setBusy(true);
    try {
      await swimService.reorderClassLevels(reordered.map((l) => l.id));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    } finally {
      setBusy(false);
    }
  }

  const sorted = [...levels].sort((a, b) => a.display_order - b.display_order);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1">Swim Skill Levels</Typography>
          <Typography variant="body2" color="text.secondary">
            Skill ladder for placing swimmers. Kids and Adults have separate ladders — switch tabs to manage each.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New {audience === 'adult' ? 'Adult' : 'Kids'} Level
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Toolbar
          sx={{
            minHeight: '56px !important',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            py: { xs: 1.5, sm: 0 },
          }}
        >
          <ToggleButtonGroup
            size="small"
            value={audience}
            exclusive
            onChange={(_e, v) => v && setAudience(v)}
          >
            <ToggleButton value="kids">Kids ladder</ToggleButton>
            <ToggleButton value="adult">Adults ladder</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="Show inactive"
          />
        </Toolbar>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (() => {
          const renderOrderArrows = (lvl: ClassLevel, i: number) => (
            <Stack direction="row" spacing={0} alignItems="center">
              <IconButton disabled={i === 0 || busy} onClick={() => void move(lvl, -1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton disabled={i === sorted.length - 1 || busy} onClick={() => void move(lvl, 1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Stack>
          );

          const renderActions = (lvl: ClassLevel) => (
            <>
              <IconButton onClick={() => openEdit(lvl)} disabled={busy} title="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              {lvl.is_active ? (
                <IconButton onClick={() => void deactivate(lvl)} disabled={busy} title="Deactivate (keeps history)">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : (
                <>
                  <IconButton
                    onClick={() => void reactivate(lvl)}
                    disabled={busy}
                    color="primary"
                    title="Reactivate"
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => void purge(lvl)}
                    disabled={busy}
                    color="error"
                    title="Permanently delete"
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </>
          );

          const renderStatusChip = (lvl: ClassLevel) => (
            <Chip
              size="small"
              label={lvl.is_active ? 'active' : 'inactive'}
              color={lvl.is_active ? 'success' : 'default'}
              variant={lvl.is_active ? 'filled' : 'outlined'}
            />
          );

          const indexOf = (lvl: ClassLevel) => sorted.findIndex((l) => l.id === lvl.id);

          const columns: ResponsiveTableColumn<ClassLevel>[] = [
            {
              key: 'order',
              label: 'Order',
              width: 60,
              renderCell: (lvl) => renderOrderArrows(lvl, indexOf(lvl)),
            },
            {
              key: 'name',
              label: 'Name',
              renderCell: (lvl) => (
                <>
                  <Typography variant="body2"><strong>{lvl.name}</strong></Typography>
                  {lvl.description && (
                    <Typography variant="caption" color="text.secondary">{lvl.description}</Typography>
                  )}
                </>
              ),
            },
            {
              key: 'prereq',
              label: 'Prerequisite',
              renderCell: (lvl) => <>{lvl.prerequisite_level?.name ?? '—'}</>,
            },
            {
              key: 'age',
              label: 'Age range',
              renderCell: (lvl) => (
                <>{lvl.min_age != null || lvl.max_age != null
                  ? `${lvl.min_age ?? '?'} – ${lvl.max_age ?? '?'}`
                  : '—'}</>
              ),
            },
            { key: 'status', label: 'Status', renderCell: renderStatusChip },
            { key: 'actions', label: 'Actions', align: 'right', renderCell: renderActions },
          ];

          return (
            <ResponsiveTable<ClassLevel>
              rows={sorted}
              columns={columns}
              rowKey={(lvl) => lvl.id}
              emptyState={<Typography color="text.secondary">No levels.</Typography>}
              renderMobileCard={(lvl) => (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1"><strong>{lvl.name}</strong></Typography>
                      {lvl.description && (
                        <Typography variant="caption" color="text.secondary">{lvl.description}</Typography>
                      )}
                    </Box>
                    {renderStatusChip(lvl)}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Prerequisite: {lvl.prerequisite_level?.name ?? '—'}
                    {(lvl.min_age != null || lvl.max_age != null) &&
                      ` • Age ${lvl.min_age ?? '?'}–${lvl.max_age ?? '?'}`}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {renderOrderArrows(lvl, indexOf(lvl))}
                    <Stack direction="row" spacing={0.5}>{renderActions(lvl)}</Stack>
                  </Stack>
                </Stack>
              )}
            />
          );
        })()}
      </Paper>

      <Dialog open={dialogMode !== null} onClose={() => setDialogMode(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{dialogMode === 'create' ? 'New Class Level' : 'Edit Class Level'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Ladder</InputLabel>
              <Select
                value={form.target_audience}
                label="Ladder"
                onChange={(e) =>
                  setForm({ ...form, target_audience: e.target.value as 'kids' | 'adult' })
                }
              >
                <MenuItem value="kids">Kids</MenuItem>
                <MenuItem value="adult">Adults</MenuItem>
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Min age"
                type="number"
                value={form.min_age}
                onChange={(e) => setForm({ ...form, min_age: e.target.value })}
                fullWidth
              />
              <TextField
                label="Max age"
                type="number"
                value={form.max_age}
                onChange={(e) => setForm({ ...form, max_age: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Prerequisite level</InputLabel>
              <Select
                value={form.prerequisite_level_id}
                label="Prerequisite level"
                onChange={(e) => setForm({ ...form, prerequisite_level_id: e.target.value })}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {sorted
                  .filter((l) => l.id !== editingId)
                  .map((l) => (
                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)} disabled={busy}>Cancel</Button>
          <Button onClick={() => void save()} variant="contained" disabled={busy || !form.name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClassLevelsAdminPage;
